/**
 * Die Bruecke zwischen der reinen Grammatik (`adresse.ts`) und dem Router.
 *
 * HIER und nur hier lebt die Push/Replace-Politik aus § 4 der Spezifikation. Wer sie an den
 * Aufrufstellen verstreute, muesste sie an zwoelf Stellen richtig halten; hier ist sie ein
 * Vorgabewert mit einer benannten Ausnahme.
 *
 * KEIN `<Routes>`: der Hook liest den Pfad selbst ueber `adresse.lesen`. Das ist die bewusste
 * Wegwahl (Spezifikation, Zuschnitt Punkt 5) - `App.tsx` bleibt unzerlegt.
 */
import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router";
import { lesen, schreiben, type Ansicht } from "./adresse.ts";

/**
 * Entweder ein fertiges Teilobjekt ODER eine Funktion, die den Stand nimmt, auf dem `schreiben`
 * GERADE rechnet (siehe `aktuellerStand` unten) und ihrerseits ein Teilobjekt liefert.
 *
 * Reicht ein fertiges Objekt, wenn die neuen Werte unabhaengig vom bisherigen Stand feststehen
 * (der ueberwiegende Fall - ein Klick auf einen Reiter, ein Filter aus einem Formularfeld). Die
 * Funktionsform braucht es NUR, wenn ein Handler mehrere `setzeAnsicht`-Aufrufe hintereinander
 * absetzt und der zweite auf dem Ergebnis des ersten aufbauen soll (siehe Kommentar bei
 * `aktuellerStand`) - dann darf er nicht das `ansicht` aus dem Funktionsrumpf schliessen, das ist
 * zu diesem Zeitpunkt noch der alte, committete Stand.
 */
export type Ansichtsaenderung = Partial<Ansicht> | ((alt: Ansicht) => Partial<Ansicht>);

export interface AnsichtsZugriff {
  ansicht: Ansicht;
  setzeAnsicht: (aenderung: Ansichtsaenderung, optionen?: { ersetzen?: boolean }) => void;
}

export function useAnsicht(): AnsichtsZugriff {
  const ort = useLocation();
  const fahre = useNavigate();

  const ansicht = useMemo(
    // `.replace(/^\?/, "")` ist defensiv, nicht tragend: `URLSearchParams` (in `lesAbfrage`,
    // `adresse.ts`) streift ein fuehrendes "?" ohnehin selbst ab, ein Verzicht auf diese Zeile
    // wuerde also nichts kaputtmachen. Sie bleibt trotzdem stehen, um das nicht stillschweigend
    // vom Verhalten einer fremden Klasse abhaengig zu machen.
    () => lesen(ort.pathname, ort.search.replace(/^\?/, "")),
    [ort.pathname, ort.search],
  );

  /**
   * Der Stand, auf dem der NAECHSTE `setzeAnsicht`-Aufruf rechnet - bewusst NICHT `ansicht`
   * selbst UND bewusst KEIN Ref (mehr, siehe Fixrunde 5 unten). `ansicht` ist ueber `useLocation`
   * an Reacts committeten Zustand gebunden und zieht erst mit dem naechsten Rendern nach. Loest
   * ein Handler zwei `setzeAnsicht`-Aufrufe HINTEREINANDER aus (noch vor jedem erneuten Rendern),
   * rechnete ein zweiter Aufruf auf demselben veralteten `ansicht` wie der erste - sein
   * `fahre(...)` ueberschriebe den ersten dann vollstaendig, statt auf ihm aufzubauen.
   *
   * FIXRUNDE 5 (Auftrag "Bug 'Bestellungen suchen'"), KRITISCHER BEFUND AN DER FIXRUNDE-2-LOESUNG:
   * bis hierher stand der Stand in einem `useRef`, synchronisiert ueber einen `useEffect(() => {
   * aktuellerStand.current = ansicht }, [ansicht])` - das funktioniert nur INNERHALB EINER
   * `useAnsicht()`-INSTANZ. `App.tsx` UND `BoardSeite.tsx` (ebenso `ZeitstrahlSeite.tsx`,
   * `LieferantenSeite.tsx`, `ZahlungsSeite.tsx`) rufen den Hook aber JEWEILS SELBST auf - jede
   * Instanz fuehrt IHREN EIGENEN Ref, keine gemeinsame Wahrheitsquelle. Feuern zwei Aufrufer
   * INNERHALB DESSELBEN synchronen Ereignisses (bevor React zwischendurch neu rendert und BEIDE
   * Sync-Effekte laufen liessen) je EINEN `setzeAnsicht`-Aufruf - z. B. `App.tsx#drawerOeffnen`
   * (oeffnet die geklickte Bestellung) UND `BoardSeite.tsx`s entprellter Suchschreiber
   * (`useEntprelltenAdresswert`, schreibt `board.q` 300 ms nach der letzten Eingabe) im selben
   * Tick - liest der ZWEITE Aufruf seinen EIGENEN, noch nicht nachgezogenen Ref und ueberschreibt
   * die Aenderung des ERSTEN vollstaendig, ohne sie zu kennen. GEMESSEN
   * (`test/komponenten/useAnsicht.test.tsx`, Beschreibung "zwei getrennte Aufrufer im selben
   * Ereignis (Fixrunde 5)", vor diesem Fix rot): eine `bestellung`, die eine Komponente gerade
   * geoeffnet hat, verschwand spurlos aus der Adresse, sobald eine ANDERE Komponente unmittelbar
   * danach (selber Tick) `board.q` schrieb - `window.location` stand danach auf `/board?q=gesucht`
   * statt `/board/bestellung/B-1?q=gesucht`. Am Board bedeutet dasselbe Muster
   * in der Gegenrichtung: ein `App.tsx`-Aufruf, der im selben Tick wie der Suchschreiber feuert,
   * kann `board.q` aus der Adresse werfen - der gerade eingetippte Suchbegriff verschwindet, die
   * Liste zeigt wieder ALLE (ungefilterten) Bestellungen, bis ein `F5` die Adresse (die dann
   * tatsaechlich keinen Suchbegriff mehr traegt, siehe unten) frisch liest. Genau das Bild aus der
   * Bugmeldung "beim Eingeben einer Bestellung kommen manchmal alle Bestellungen ... hoch".
   *
   * DER FIX: `window.location` ist NICHT an eine Hook-Instanz gebunden, sondern die EINE, fuer
   * ALLE `useAnsicht()`-Aufrufer GEMEINSAME Adresse - UND sie zieht (anders als `ansicht` oben)
   * NICHT erst mit dem naechsten Rendern nach: `fahre(...)`/`history.pushState`/`replaceState`
   * schreiben `window.location` SYNCHRON, noch bevor React ueberhaupt neu rendert (belegt im
   * selben Sondentest: `window.location.pathname` zeigt den ERSTEN Aufruf bereits, wenn der ZWEITE
   * noch gar nicht gelaufen ist). `alt` liest deshalb JETZT direkt `window.location` statt eines
   * Instanz-Refs - das macht den frueheren Ref UND seinen Sync-Effekt ueberfluessig, nicht nur
   * fuer Doppelaufrufe DERSELBEN Instanz (die Fixrunde-1-4-Faelle bleiben davon unberuehrt gruen),
   * sondern jetzt auch INSTANZUEBERGREIFEND.
   *
   * WICHTIG (Fixrunde 2, weiterhin gueltig): `alt` traegt NICHT, was ein frueherer Aufrufer
   * WOLLTE, sondern was die daraus erzeugte ADRESSE tatsaechlich wiedergibt (`lesen(...)` auf dem
   * echten `window.location`) - eine Aenderung, die auf der aktuellen Seite gar nicht in die
   * Adresse passt (z. B. ein Board-Filter, waehrend die Kostenseite offen ist, die keinen
   * Board-Block schreibt, siehe `adresse.ts` `schreibAbfrage`), verschwindet damit weiterhin
   * sofort, statt in einer zweiten Wahrheitsquelle liegenzubleiben.
   */
  const setzeAnsicht = useCallback(
    (aenderung: Ansichtsaenderung, optionen?: { ersetzen?: boolean }) => {
      const alt = lesen(window.location.pathname, window.location.search.replace(/^\?/, ""));
      const teilaenderung = typeof aenderung === "function" ? aenderung(alt) : aenderung;
      const neu = { ...alt, ...teilaenderung };
      const url = schreiben(neu);
      // KEINE Zweitaufruf-Erkennung (Fixrunde 3, Ruecknahme der Fixrunde-2-Regel): "Ausgangsstand
      // ungleich `ansicht`" ist KEIN verlaesslicher Marker fuer einen zweiten Aufruf im selben
      // Ereignis - erzeugt ein Aufruf dieselbe Adresse (z.B. derselbe Filterwert erneut, ein
      // bereits aktiver Chip), aendern sich pathname/search nicht, das `useMemo` oben behaelt das
      // ALTE `ansicht`-Objekt - ein voellig unabhaengiger, spaeterer Aufruf ersetzte dann faelschlich
      // statt zu schieben, und die Zurueck-Taste verloere einen Eintrag. Zwei Aufrufe im selben
      // Ereignis erzeugen deshalb BEWUSST zwei Verlaufseintraege (siehe Testfall in
      // useAnsicht.test.tsx) - der richtige Weg fuer einen Aufrufer, der mehrere Aenderungen in
      // einem Zug braucht, ist EIN `setzeAnsicht`-Aufruf (die Funktionsform erlaubt das, wenn die
      // zweite Aenderung vom Ergebnis der ersten abhaengt), nicht zwei.
      fahre(url, { replace: optionen?.ersetzen === true });
    },
    [fahre],
  );

  return { ansicht, setzeAnsicht };
}
